import { AxiosResponse } from "axios";
import { post, put, del } from "@/services/axiosUitils";
import { getFoodApiConfig, getFoodApiEndpoints } from "../config/foodApiConfig";
import {
  categorizeFoodError,
  logFoodError,
  shouldRetryFoodError,
  getUserFriendlyFoodMessage,
  getFoodErrorRetryDelay,
} from "../utils/foodApiErrors";
import {
  parseFoodPredictionResponse,
  parseMealOperationResponse,
  parseUploadResponse,
} from "../utils/foodResponseParser";
import {
  FoodCacheManager,
  generateFileHash,
  generateImageHash,
} from "../utils/foodCache";
import type {
  FoodOperationOptions,
  ProcessedFile,
  SaveMealPayload as ImportedSaveMealPayload,
} from "../types/foodApiTypes";

export interface FoodItem {
  name: string;
  serving_size_g: number;
  serving_description: string;
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

export interface FoodItemWithId extends FoodItem {
  food_item_id: string;
}

export interface ManualFoodPayload {
  name: string;
  container: string;
  size: string;
  count: number;
}

export interface EditManualFoodPayload {
  name: string;
  serving_size_g: number;
  serving_description: string;
}

export interface AddFoodItemPayload {
  meal_id: string;
  name: string;
  serving_size_g: number;
  serving_description: string;
}

export interface EditFoodItemPayload {
  name: string;
  serving_size_g: number;
  serving_description: string;
}

export interface AddFoodItemResponse {
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  message: string;
  added_item: {
    food_item_id: string;
    name: string;
    serving_size_g: number;
    serving_description: string;
  };
}

export interface EditFoodItemResponse {
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  message: string;
}

export interface DeleteFoodItemResponse {
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  message: string;
}

export interface NutritionTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

// Use imported SaveMealPayload from types instead of duplicate definition
export type SaveMealPayload = ImportedSaveMealPayload;

export interface SaveMealResponse {
  message: string;
  meal_id: string;
  food_items: FoodItemWithId[];
  ts: string;
  image_url: string;
  scan_type: string;
  file_utility: {
    document_id: string;
    original_file: string;
    optimized_file: string;
    thumbnail_file: string;
  };
}

export interface PredictFoodResponse {
  id: string;
  document_id: string; // ✅ Added at root level
  food_items: FoodItem[];
  ts: string;
  image_url: string;
  file_utility?: {
    // Made optional since it might not exist
    document_id: string;
    original_file: string;
    optimized_file: string;
    thumbnail_file: string;
    file_hash: string;
    file_size: number;
    file_type: string;
  };
}

// GetImageResponse interface moved to types file for better organization

// Professional Food API Service with enhanced architecture
export class FoodApiService {
  private static config = getFoodApiConfig();
  private static endpoints = getFoodApiEndpoints();

  /**
   * File validation and processing
   */
  private static async processFile(file: File): Promise<ProcessedFile> {
    const config = this.config;
    const hash = await generateFileHash(file);

    const processedFile: ProcessedFile = {
      file,
      hash,
      metadata: {
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
      },
      validation: {
        isValidType: config.UPLOAD.ALLOWED_TYPES.includes(file.type),
        isValidSize: file.size <= config.UPLOAD.MAX_FILE_SIZE,
        errors: [],
      },
    };

    // Validate file type
    if (!processedFile.validation.isValidType) {
      processedFile.validation.errors.push(
        `Invalid file type. Allowed: ${config.UPLOAD.ALLOWED_TYPES.join(", ")}`
      );
    }

    // Validate file size
    if (!processedFile.validation.isValidSize) {
      processedFile.validation.errors.push(
        `File too large. Maximum size: ${config.UPLOAD.MAX_FILE_SIZE / (1024 * 1024)}MB`
      );
    }

    return processedFile;
  }

  /**
   * Enhanced food prediction with caching and error handling
   */
  static async predictFood(
    input: File | string,
    options: FoodOperationOptions = {}
  ): Promise<AxiosResponse<{ data: PredictFoodResponse }>> {
    try {
      // Handle string input (document ID)
      if (typeof input === "string") {
        // Check cache first
        if (options.useCache !== false) {
          const imageHash = generateImageHash(input);
          const cached = FoodCacheManager.getFoodPrediction(imageHash);
          if (cached) {
            return { data: { data: cached } } as AxiosResponse<{
              data: PredictFoodResponse;
            }>;
          }
        }

        const maxRetries = this.config.RETRY_COUNT;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const payload = { doc_id: input };
            const response = await post(
              this.endpoints.analyzeFoodImage,
              payload
            );

            // Parse and validate response
            const parsedResponse = parseFoodPredictionResponse(response);
            if (parsedResponse.success) {
              // Cache the result
              const imageHash = generateImageHash(input);
              FoodCacheManager.setFoodPrediction(
                imageHash,
                parsedResponse.data
              );

              // Return both parsed data AND raw response for localStorage
              return { 
                data: { 
                  data: parsedResponse.data,
                  _rawResponse: response // Preserve original API response
                } as any
              } as AxiosResponse<{
                data: PredictFoodResponse;
              }>;
            }

            throw new Error("Failed to parse food prediction response");
          } catch (error) {
            const foodError = categorizeFoodError(
              error,
              this.endpoints.analyzeFoodImage,
              {
                input_type: "document_id",
                document_id: input,
              }
            );

            if (!shouldRetryFoodError(foodError, attempt, maxRetries)) {
              logFoodError();
              throw new Error(getUserFriendlyFoodMessage(foodError));
            }

            const delay = getFoodErrorRetryDelay(foodError, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }

        throw new Error("Food prediction failed after all retries");
      }

      // Handle File input
      const processedFile = await this.processFile(input);

      // Validate file
      if (processedFile.validation.errors.length > 0) {
        throw new Error(
          `File validation failed: ${processedFile.validation.errors.join(", ")}`
        );
      }

      // Check cache for file hash
      if (options.useCache !== false) {
        const cached = FoodCacheManager.getFoodPrediction(processedFile.hash);
        if (cached) {
          return { data: { data: cached } } as AxiosResponse<{
            data: PredictFoodResponse;
          }>;
        }
      }

      const maxRetries = this.config.RETRY_COUNT;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Upload file first
          const formData = new FormData();
          formData.append("folder_name", this.config.UPLOAD.FOLDER_NAME);
          formData.append("file_path", processedFile.file);

          const uploadResponse = await post(
            this.endpoints.uploadFile,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: this.config.UPLOAD_TIMEOUT,
            }
          );

          // Parse upload response
          const uploadResult = parseUploadResponse(uploadResponse);
          if (!uploadResult.success) {
            throw new Error("Upload parsing failed");
          }

          // Cache upload result
          FoodCacheManager.setUploadResult(
            processedFile.hash,
            uploadResult.data
          );

          // Analyze uploaded image
          const analyzePayload = { doc_id: uploadResult.data.document_id };

          const analyzeResponse = await post(
            this.endpoints.analyzeFoodImage,
            analyzePayload
          );

          // Parse prediction response
          const parsedResponse = parseFoodPredictionResponse(analyzeResponse);

          if (parsedResponse.success) {
            // Cache the prediction
            FoodCacheManager.setFoodPrediction(
              processedFile.hash,
              parsedResponse.data
            );

            // Return both parsed data AND raw response for localStorage
            return {
              data: { 
                data: parsedResponse.data,
                _rawResponse: analyzeResponse // Preserve original API response
              } as any
            } as AxiosResponse<{
              data: PredictFoodResponse;
            }>;
          }

          throw new Error("Failed to parse food prediction response");
        } catch (error) {
          const foodError = categorizeFoodError(
            error,
            this.endpoints.analyzeFoodImage,
            {
              input_type: "file",
              file_name: processedFile.file.name,
              file_size: processedFile.file.size,
            }
          );

          if (!shouldRetryFoodError(foodError, attempt, maxRetries)) {
            logFoodError();
            throw new Error(getUserFriendlyFoodMessage(foodError));
          }

          const delay = getFoodErrorRetryDelay(foodError, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      throw new Error("Food prediction failed after all retries");
    } catch (error) {
      const foodError = categorizeFoodError(
        error,
        this.endpoints.analyzeFoodImage
      );
      logFoodError();
      throw new Error(getUserFriendlyFoodMessage(foodError));
    }
  }

  /**
   * Add food item to meal with enhanced error handling
   */
  static async addFoodItem(
    payload: AddFoodItemPayload,
    _options: FoodOperationOptions = {}
  ): Promise<AxiosResponse<AddFoodItemResponse>> {
    try {
      const response = await post(this.endpoints.addFoodItems, payload, {
        timeout: this.config.TIMEOUT,
      });

      // Parse and validate response
      const parsedResponse = parseMealOperationResponse<AddFoodItemResponse>(
        response,
        "add"
      );
      if (parsedResponse.success) {
        // Invalidate meal cache if meal_id exists
        if (payload.meal_id) {
          FoodCacheManager.invalidateMeal(payload.meal_id);
        }

        return response as AxiosResponse<AddFoodItemResponse>;
      }

      throw new Error("Failed to parse add food item response");
    } catch (error) {
      const foodError = categorizeFoodError(
        error,
        this.endpoints.addFoodItems,
        {
          operation: "add_food_item",
          meal_id: payload.meal_id,
        }
      );

      logFoodError();
      throw new Error(getUserFriendlyFoodMessage(foodError));
    }
  }

  /**
   * Edit food item with enhanced error handling
   */
  static async editFoodItem(
    foodItemId: string,
    payload: EditFoodItemPayload,
    _options: FoodOperationOptions = {}
  ): Promise<AxiosResponse<EditFoodItemResponse>> {
    try {
      const response = await put(
        this.endpoints.editFoodItems(foodItemId),
        payload,
        {
          timeout: this.config.TIMEOUT,
        }
      );

      const parsedResponse = parseMealOperationResponse<EditFoodItemResponse>(
        response,
        "edit"
      );
      if (parsedResponse.success) {
        return response as AxiosResponse<EditFoodItemResponse>;
      }

      throw new Error("Failed to parse edit food item response");
    } catch (error) {
      const foodError = categorizeFoodError(
        error,
        this.endpoints.editFoodItems(foodItemId),
        {
          operation: "edit_food_item",
          food_item_id: foodItemId,
        }
      );

      logFoodError();
      throw new Error(getUserFriendlyFoodMessage(foodError));
    }
  }

  /**
   * Delete food item with enhanced error handling
   */
  static async deleteFoodItem(
    foodItemId: string,
    _options: FoodOperationOptions = {}
  ): Promise<AxiosResponse<DeleteFoodItemResponse>> {
    try {
      const response = await del(this.endpoints.deleteFoodItems(foodItemId), {
        timeout: this.config.TIMEOUT,
      });

      const parsedResponse = parseMealOperationResponse<DeleteFoodItemResponse>(
        response,
        "delete"
      );
      if (parsedResponse.success) {
        return response as AxiosResponse<DeleteFoodItemResponse>;
      }

      throw new Error("Failed to parse delete food item response");
    } catch (error) {
      const foodError = categorizeFoodError(
        error,
        this.endpoints.deleteFoodItems(foodItemId),
        {
          operation: "delete_food_item",
          food_item_id: foodItemId,
        }
      );

      logFoodError();
      throw new Error(getUserFriendlyFoodMessage(foodError));
    }
  }

  /**
   * Save meal with enhanced error handling and caching
   */
  static async saveMeal(
    payload: SaveMealPayload,
    options: FoodOperationOptions = {}
  ): Promise<AxiosResponse<SaveMealResponse>> {
    try {
      // Check cache first
      if (options.useCache !== false && payload.document_id) {
        const cached = FoodCacheManager.getMealData(payload.document_id);
        if (cached) {
          return { data: cached } as AxiosResponse<SaveMealResponse>;
        }
      }

      const { axiosConfig } = await import("@/services/axiosConfig");
      const response = await axiosConfig.post(
        this.endpoints.saveMeal,
        payload,
        {
          timeout: this.config.TIMEOUT,
        }
      );

      const parsedResponse = parseMealOperationResponse<SaveMealResponse>(
        response.data,
        "save"
      );
      if (parsedResponse.success) {
        // Cache the meal data
        FoodCacheManager.setMealData(
          parsedResponse.data.meal_id,
          parsedResponse.data
        );

        return response as AxiosResponse<SaveMealResponse>;
      }

      throw new Error("Failed to parse save meal response");
    } catch (error) {
      const foodError = categorizeFoodError(error, this.endpoints.saveMeal, {
        operation: "save_meal",
        document_id: payload.document_id,
      });

      logFoodError();
      throw new Error(getUserFriendlyFoodMessage(foodError));
    }
  }

  /**
   * Get image URL with caching
   */
  static getImageUrl(fileName: string): string {
    // Check cache first
    const cached = FoodCacheManager.getImageUrl(fileName);
    if (cached) {
      return cached;
    }

    const imageUrl = `${this.config.API_BASE_URL}${this.endpoints.getImages(fileName)}`;

    // Cache the URL
    FoodCacheManager.setImageUrl(fileName, imageUrl);

    return imageUrl;
  }

  /**
   * Get image data (placeholder for future implementation)
   */
  static async getImage(
    _insertId: string
  ): Promise<AxiosResponse<{ data: { IMG: string } }>> {
    // TODO: Implement actual API call when axiosInstance is available
    return Promise.reject(new Error("API not implemented"));
  }

  /**
   * Add manual food item
   */
  static async addManualFood(
    payload: ManualFoodPayload,
    _options: FoodOperationOptions = {}
  ): Promise<AxiosResponse<any>> {
    try {
      const { axiosConfig } = await import("@/services/axiosConfig");
      const response = await axiosConfig.post(
        "/food_scanner/manual-food/edit/",
        payload,
        {
          timeout: this.config.TIMEOUT,
        }
      );

      return response;
    } catch (error) {
      const foodError = categorizeFoodError(error, "manual-food/edit", {
        operation: "add_manual_food",
        food_name: payload.name,
      });

      logFoodError();
      throw new Error(getUserFriendlyFoodMessage(foodError));
    }
  }

  /**
   * Edit manual food item
   */
  static async editManualFood(
    payload: EditManualFoodPayload,
    _options: FoodOperationOptions = {}
  ): Promise<AxiosResponse<any>> {
    try {
      const { axiosConfig } = await import("@/services/axiosConfig");
      const response = await axiosConfig.post(
        "/food_scanner/manual-food/edit/",
        payload,
        {
          timeout: this.config.TIMEOUT,
        }
      );

      return response;
    } catch (error) {
      const foodError = categorizeFoodError(error, "manual-food/edit", {
        operation: "edit_manual_food",
        food_name: payload.name,
      });

      logFoodError();
      throw new Error(getUserFriendlyFoodMessage(foodError));
    }
  }

  /**
   * Clear all food caches
   */
  static clearCache(): void {
    FoodCacheManager.clearAll();
  }

  /**
   * Get cache statistics for monitoring
   */
  static getCacheStats() {
    return FoodCacheManager.getStats();
  }

  /**
   * Force cleanup of expired cache entries
   */
  static forceCleanupCache(): number {
    return FoodCacheManager.cleanup();
  }
}

// Export backward-compatible object for existing code
export const foodApiService = {
  predictFood: (input: File | string) => FoodApiService.predictFood(input),
  addFoodItem: (payload: AddFoodItemPayload) =>
    FoodApiService.addFoodItem(payload),
  editFoodItem: (foodItemId: string, payload: EditFoodItemPayload) =>
    FoodApiService.editFoodItem(foodItemId, payload),

  deleteFoodItem: (foodItemId: string) =>
    FoodApiService.deleteFoodItem(foodItemId),
  saveMeal: (payload: SaveMealPayload) => FoodApiService.saveMeal(payload),
  getImageUrl: (fileName: string) => FoodApiService.getImageUrl(fileName),
  getImage: (insertId: string) => FoodApiService.getImage(insertId),
  addManualFood: (payload: ManualFoodPayload) =>
    FoodApiService.addManualFood(payload),
  editManualFood: (payload: EditManualFoodPayload) =>
    FoodApiService.editManualFood(payload),
};

// Export default singleton for City Collab pattern consistency
export default foodApiService;
